import type { SpacingToken } from '../../../themes/types';
import type { StyleResolverContext, CssFragment } from './index';
import type { SpacingValue } from './layout';
import { resolveSpacingValue } from './layout';

// ============================================================================
// Position detection
// ============================================================================

/** Derive CSS position from binary props. Priority: sticky -> fixed -> absoluteFill -> absolute -> relative. */
const getPositionFromProps = (props: {
  sticky?: boolean;
  fixed?: boolean;
  absoluteFill?: boolean;
  absolute?: boolean;
  relative?: boolean;
}): string | undefined => {
  if (props.sticky) return 'sticky';
  if (props.fixed) return 'fixed';
  if (props.absoluteFill) return 'absolute';
  if (props.absolute) return 'absolute';
  if (props.relative) return 'relative';
  return undefined;
};

// ============================================================================
// Types
// ============================================================================

export interface PositionValueProps {
  top?: SpacingValue;
  left?: SpacingValue;
  bottom?: SpacingValue;
  right?: SpacingValue;
}

export interface ResolvedPositioning {
  top: string | undefined;
  left: string | undefined;
  bottom: string | undefined;
  right: string | undefined;
}

export interface PositioningProps {
  sticky?: boolean;
  fixed?: boolean;
  absoluteFill?: boolean;
  absolute?: boolean;
  relative?: boolean;
  top?: SpacingValue;
  left?: SpacingValue;
  bottom?: SpacingValue;
  right?: SpacingValue;
}

// ============================================================================
// Resolution helpers
// ============================================================================

const resolvePositioningValues = (
  props: PositionValueProps,
  getSpacing: (token: SpacingToken) => string,
): ResolvedPositioning => ({
  top: resolveSpacingValue(props.top, getSpacing),
  left: resolveSpacingValue(props.left, getSpacing),
  bottom: resolveSpacingValue(props.bottom, getSpacing),
  right: resolveSpacingValue(props.right, getSpacing),
});

/** Returns CSS fragments for position and inset (top/left/bottom/right). */
const resolvePositioningCss = (props: PositioningProps, getSpacing: (token: SpacingToken) => string): CssFragment[] => {
  const position = getPositionFromProps(props);
  const inset = resolvePositioningValues(props, getSpacing);
  return [
    position ? `position: ${position};` : null,
    props.absoluteFill ? 'top: 0; left: 0; right: 0; bottom: 0;' : null,
    !props.absoluteFill && inset.top ? `top: ${inset.top};` : null,
    !props.absoluteFill && inset.left ? `left: ${inset.left};` : null,
    !props.absoluteFill && inset.bottom ? `bottom: ${inset.bottom};` : null,
    !props.absoluteFill && inset.right ? `right: ${inset.right};` : null,
  ];
};

// ============================================================================
// Picker + style resolver
// ============================================================================

const pickPositioningProps = <T extends Partial<PositioningProps>>(props: T): PositioningProps => {
  const { sticky, fixed, absoluteFill, absolute, relative, top, left, bottom, right } = props;
  return { sticky, fixed, absoluteFill, absolute, relative, top, left, bottom, right };
};

export const resolvePositioningStyleCss = (
  props: Record<string, unknown>,
  theme: StyleResolverContext,
): CssFragment[] => resolvePositioningCss(pickPositioningProps(props as Partial<PositioningProps>), theme.getSpacing);
